import type { ActionKind } from '@fafi/shared';
import { DT, dequantPlayer, dequantBall, type DecodedSnapshot } from '@fafi/shared';

// Stream-anchored interpolation (§10.3 — ★redefined). The render timeline anchors to the RECEIVED
// snapshot stream, not server-now: renderServerTime = newestSnapshotServerTime − INTERP_BACK, where
// INTERP_BACK = 100 ms (2 snapshot intervals) + adaptive jitter margin (p99 inter-arrival jitter, capped
// 50 ms). This guarantees a bracketing pair regardless of transit delay (the old serverNow−100 ms
// definition starves at ≥ 100 ms one-way, §23). Non-predicted entities lerp pos / slerp facing between
// brackets and NEVER extrapolate; the ball may extrapolate ballistically ≤ EXTRAP_MAX then holds.

const TICK_MS = DT * 1000;
// D65b — the interpolation delay is now ADAPTIVE to the measured snapshot cadence, not a fixed 100 ms.
// Rendering every remote entity + the ball ~100 ms in the past (on top of RTT) was the felt "lag" vs the
// zero-latency kickabout. INTERP_BACK = INTERP_BACK_INTERVALS × the measured snapshot interval, clamped
// to [FLOOR, CAP], plus the jitter margin. At the old 15 Hz cadence (interval 66.7 ms) this is 100 ms —
// bit-for-bit unchanged. When the server sends 30 Hz (interval 33.3 ms, D65b for ≤5v5) it drops to ~50 ms,
// halving the world delay while still holding ≥ 1 bracketing interval (no starvation, T-NET-002).
export const INTERP_BACK_INTERVALS = 1.5;
export const INTERP_BACK_FLOOR_MS = 50;
export const INTERP_BACK_CAP_MS = 100;
export const JITTER_MARGIN_MAX_MS = 50;
export const EXTRAP_MAX_MS = 50; // ball only
export const TIMELINE_SLEW_MS = 2; // per rendered frame
/** D62: beyond this much timeline error, SNAP instead of slewing. Slewing 2 ms/frame cannot catch a
 *  stalled or jumped stream (it loses ground every frame), so the timeline ran away unboundedly and
 *  then rendered the oldest held snapshot for as long again while it crawled back. */
export const TIMELINE_RESYNC_MS = 500;

interface Held {
  serverTimeMs: number;
  arrivalMs: number;
  snap: DecodedSnapshot;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
/** Shortest-arc angle interpolation (facing is periodic). */
function slerpAngle(a: number, b: number, t: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  else if (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

export interface RenderedEntity {
  pos: { x: number; y: number };
  facing: number;
  z: number;
  extrapolated: boolean;
  /** D126 — the animation state belonging to the pose being rendered. Taking these from the NEWEST
   *  snapshot (as the caller used to) starts the clip 50–100 ms before the body it belongs to moves:
   *  the keeper's dive animation began while he was still standing where the interpolator had him. */
  action: ActionKind;
  actionTicks: number;
  moveSpeed: number;
}

export class Interpolator {
  private readonly held: Held[] = []; // ascending serverTime, last 32
  private renderServerTimeMs = -1;
  private lastArrivalMs = -1;
  private lastServerTimeMs = -1;
  private readonly jitterWindow: number[] = [];
  private readonly snapIntervalWindow: number[] = []; // D65b: recent snapshot serverTime gaps (the cadence)
  private readonly cap = 32;

  /** D46 Item 6: forget the old-epoch timeline for a NEW match (a rematch restarts at tick 0). */
  reset(): void {
    this.held.length = 0;
    this.renderServerTimeMs = -1;
    this.lastArrivalMs = -1;
    this.lastServerTimeMs = -1;
    this.jitterWindow.length = 0;
    this.snapIntervalWindow.length = 0;
  }

  onSnapshot(snap: DecodedSnapshot, arrivalMs: number): void {
    const serverTimeMs = snap.tick * TICK_MS;
    if (this.lastArrivalMs >= 0 && this.lastServerTimeMs >= 0) {
      const expectedGap = serverTimeMs - this.lastServerTimeMs; // stream cadence
      const actualGap = arrivalMs - this.lastArrivalMs;
      const jitter = Math.abs(actualGap - expectedGap);
      this.jitterWindow.push(jitter);
      if (this.jitterWindow.length > 64) this.jitterWindow.shift();
      if (expectedGap > 0) {
        this.snapIntervalWindow.push(expectedGap); // D65b: the server cadence drives INTERP_BACK
        if (this.snapIntervalWindow.length > 16) this.snapIntervalWindow.shift();
      }
    }
    this.lastArrivalMs = arrivalMs;
    this.lastServerTimeMs = serverTimeMs;
    // insert keeping ascending serverTime (TCP ⇒ already ordered; defensive)
    this.held.push({ serverTimeMs, arrivalMs, snap });
    this.held.sort((a, b) => a.serverTimeMs - b.serverTimeMs);
    while (this.held.length > this.cap) this.held.shift();
    if (this.renderServerTimeMs < 0) this.renderServerTimeMs = this.targetRenderTimeMs(); // seed on first snapshot
  }

  /** p99 inter-arrival jitter over the window, capped to [0, JITTER_MARGIN_MAX_MS]. */
  private jitterMarginMs(): number {
    if (this.jitterWindow.length === 0) return 0;
    const sorted = [...this.jitterWindow].sort((a, b) => a - b);
    const p99 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.99))]!;
    return Math.max(0, Math.min(JITTER_MARGIN_MAX_MS, p99));
  }

  get newestServerTimeMs(): number {
    return this.held.length ? this.held[this.held.length - 1]!.serverTimeMs : 0;
  }
  get hasSnapshots(): boolean {
    return this.held.length > 0;
  }
  /** D65b: the measured snapshot interval (median of recent serverTime gaps), or the 15 Hz default until
   *  two snapshots have arrived. The server cadence is deterministic, so the median is stable. */
  private snapIntervalMs(): number {
    if (this.snapIntervalWindow.length === 0) return 2 * TICK_MS; // default = the old 15 Hz cadence
    const sorted = [...this.snapIntervalWindow].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)]!;
  }
  /** D65b: INTERP_BACK base, adaptive to the cadence — clamped so a slow/fast stream stays sane. */
  private interpBackBaseMs(): number {
    return Math.max(INTERP_BACK_FLOOR_MS, Math.min(INTERP_BACK_CAP_MS, INTERP_BACK_INTERVALS * this.snapIntervalMs()));
  }
  private targetRenderTimeMs(): number {
    return this.newestServerTimeMs - (this.interpBackBaseMs() + this.jitterMarginMs());
  }
  get interpBackMs(): number {
    return this.interpBackBaseMs() + this.jitterMarginMs();
  }
  get renderTimeMs(): number {
    return this.renderServerTimeMs;
  }

  /** Advance the render timeline by real frame time, then slew-correct toward the stream anchor
   *  (newestServerTime − INTERP_BACK) by ≤ TIMELINE_SLEW_MS — so it tracks real time without jumps. */
  advance(frameDeltaMs: number): void {
    if (this.held.length === 0) return;
    const target = this.targetRenderTimeMs();
    if (this.renderServerTimeMs < 0) {
      this.renderServerTimeMs = target;
      return;
    }
    this.renderServerTimeMs += frameDeltaMs; // real-time progression
    const err = target - this.renderServerTimeMs;
    // D62: bound the error. Slewing at TIMELINE_SLEW_MS (2 ms) per frame is far slower than the frame
    // time it corrects, so a stalled stream let the timeline run away without limit — and then need
    // MINUTES to crawl back once snapshots returned (a 50 s stall measured 44 s of drift and ~50 s of
    // catch-up, rendering the OLDEST held snapshot throughout). Past the resync threshold, snap.
    if (Math.abs(err) > TIMELINE_RESYNC_MS) {
      this.renderServerTimeMs = target;
      return;
    }
    this.renderServerTimeMs += Math.sign(err) * Math.min(Math.abs(err), TIMELINE_SLEW_MS);
  }

  /** Bracketing pair (a ≤ renderTime ≤ b) for the current render time; clamps at the ends. */
  private bracket(): { a: Held; b: Held; t: number } | null {
    if (this.held.length === 0) return null;
    const rt = this.renderServerTimeMs;
    if (rt <= this.held[0]!.serverTimeMs) return { a: this.held[0]!, b: this.held[0]!, t: 0 };
    const last = this.held[this.held.length - 1]!;
    if (rt >= last.serverTimeMs) return { a: last, b: last, t: 0 }; // players never extrapolate — hold newest
    for (let i = 0; i < this.held.length - 1; i++) {
      const a = this.held[i]!;
      const b = this.held[i + 1]!;
      if (rt >= a.serverTimeMs && rt <= b.serverTimeMs) {
        const span = b.serverTimeMs - a.serverTimeMs;
        return { a, b, t: span > 0 ? (rt - a.serverTimeMs) / span : 0 };
      }
    }
    return { a: last, b: last, t: 0 };
  }

  /** Rendered state of a non-predicted entity at the render time (lerp/slerp, never extrapolated). */
  entity(id: number): RenderedEntity | null {
    const br = this.bracket();
    if (!br) return null;
    const pa = br.a.snap.players.find((p) => p.id === id);
    const pb = br.b.snap.players.find((p) => p.id === id);
    if (!pa || !pb) return null;
    const va = dequantPlayer(pa);
    const vb = dequantPlayer(pb);
    // D126: the animation state comes from whichever END of the bracket the pose is nearer to, so the
    // clip is never more than half a snapshot away from the body it is animating.
    const near = br.t < 0.5 ? va : vb;
    return {
      pos: { x: lerp(va.pos.x, vb.pos.x, br.t), y: lerp(va.pos.y, vb.pos.y, br.t) },
      facing: slerpAngle(va.facing, vb.facing, br.t),
      z: lerp(va.z, vb.z, br.t),
      extrapolated: false,
      action: near.action,
      actionTicks: near.actionTicks,
      moveSpeed: Math.hypot(near.vel.x, near.vel.y),
    };
  }

  /** Rendered ball position: lerp within brackets; beyond newest, extrapolate ballistically ≤ EXTRAP_MAX then hold. */
  ball(): { pos: { x: number; y: number; z: number }; extrapolated: boolean } | null {
    if (this.held.length === 0) return null;
    const rt = this.renderServerTimeMs;
    const last = this.held[this.held.length - 1]!;
    if (rt > last.serverTimeMs) {
      const over = Math.min(rt - last.serverTimeMs, EXTRAP_MAX_MS) / 1000; // seconds, capped
      const v = dequantBall(last.snap.ball);
      return { pos: { x: v.pos.x + v.vel.x * over, y: v.pos.y + v.vel.y * over, z: v.pos.z + v.vel.z * over }, extrapolated: over > 0 };
    }
    const br = this.bracket()!;
    const ba = dequantBall(br.a.snap.ball);
    const bb = dequantBall(br.b.snap.ball);
    return {
      pos: { x: lerp(ba.pos.x, bb.pos.x, br.t), y: lerp(ba.pos.y, bb.pos.y, br.t), z: lerp(ba.pos.z, bb.pos.z, br.t) },
      extrapolated: false,
    };
  }
}
